import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { useBrand } from "@/contexts/BrandContext";
import { BusinessSwitcher } from "@/components/BusinessSwitcher";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { resolveAsset, hideOnError } from "@/lib/assets";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  LayoutDashboard, Users, Contact, Ticket, CalendarCheck2, Bell, ClipboardList,
  BarChart3, Settings, Sun, Moon, LogOut, Menu, UsersRound, Activity,
  Building2, ChevronRight, ChevronDown, MessageSquare, Shield, User as UserIcon, Zap, PlugZap, FileText, Palette,
  UserPlus, UserCheck, UserX, RefreshCcw, Circle,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useVisibilityPoll } from "@/hooks/useVisibilityPoll";
import api from "@/lib/api";
import { useBusiness } from "@/contexts/BusinessContext";
import { useLocation } from "react-router-dom";
import GlobalSearch from "@/components/GlobalSearch";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, perm: "dashboard.view", testId: "nav-dashboard" },
  // "/leads" is rendered as an expandable group (see LeadsGroup below) — the entry here is only a marker.
  { to: "/leads", label: "Leads", icon: Users, perm: "lead.view", testId: "nav-leads", group: "leads" },
  { to: "/followups", label: "Follow-ups", icon: Bell, perm: "followup.view", testId: "nav-followups" },
  { to: "/clients", label: "Clients", icon: Contact, perm: "client.view", testId: "nav-clients" },
  { to: "/chat", label: "Chat", icon: MessageSquare, perm: "chat.view", testId: "nav-chat" },
  { to: "/tickets", label: "Tickets", icon: Ticket, perm: "ticket.view", testId: "nav-tickets" },
  { to: "/meetings", label: "Meetings", icon: CalendarCheck2, perm: "meeting.view", testId: "nav-meetings" },
  { to: "/employees", label: "Employees", icon: UsersRound, perm: "employee.view", testId: "nav-employees" },
  { to: "/reports", label: "Reports", icon: BarChart3, perm: "report.view", testId: "nav-reports" },
];

const SETTINGS_LINKS = [
  { to: "/settings/integrations", label: "Integration Center", icon: PlugZap, testId: "nav-integrations" },
  { to: "/settings/facebook", label: "Facebook Accounts", icon: PlugZap, testId: "nav-fb-accounts" },
  { to: "/settings/templates", label: "Templates", icon: FileText, testId: "nav-templates" },
  { to: "/settings/businesses", label: "Businesses", icon: Building2, testId: "nav-settings-businesses" },
  { to: "/settings/statuses", label: "Statuses & Sources", icon: ClipboardList, testId: "nav-settings-statuses" },
  { to: "/settings/roles", label: "Roles", icon: Shield, testId: "nav-settings-roles" },
];

// Mobile bottom nav (limited to 5)
const MOBILE_NAV = [
  { to: "/dashboard", label: "Home", icon: LayoutDashboard, testId: "mnav-dashboard" },
  { to: "/leads", label: "Leads", icon: Users, testId: "mnav-leads" },
  { to: "/chat", label: "Chat", icon: MessageSquare, testId: "mnav-chat" },
  { to: "/notifications", label: "Alerts", icon: Bell, testId: "mnav-alerts" },
  { to: "/profile", label: "Me", icon: UserIcon, testId: "mnav-me" },
];

export default function AdminLayout() {
  const { user, logout, hasPerm, kind } = useAuth();
  const { theme, toggle } = useTheme();
  const { brand, logoSrc } = useBrand();
  const [collapsed, setCollapsed] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (kind !== "user") { navigate("/client"); return; }
  }, [kind, navigate]);
  // Poll notifications every 30 s but ONLY while the tab is visible. This alone
  // eliminates hundreds of unnecessary requests + state updates per hour when a
  // user parks the CRM in a background tab all day, which was the single
  // biggest driver of the memory blowup on production.
  useVisibilityPoll(() => {
    if (kind !== "user") return;
    api.get("/notifications", { params: { limit: 1 } }).then((r) => setUnread(r.data.unread || 0)).catch(() => {});
  }, 30000, kind === "user");

  const isSuperAdmin = user?.role === "super_admin";
  // Show all pipeline menus filtered by permission (Clients menu is now visible to Super Admin too
  // per the new "converted → Clients" workflow).
  const visibleNav = NAV.filter((n) => n.perm === "chat.view" || hasPerm(n.perm));
  return (
    <div className="min-h-screen flex bg-background">
      {/* Sidebar - desktop */}
      <aside
        className={`hidden md:flex ${collapsed ? "w-16" : "w-64"} shrink-0 border-r border-border bg-card flex-col transition-[width] duration-200`}
        data-testid="admin-sidebar"
      >
        <SidebarBody collapsed={collapsed} setCollapsed={setCollapsed}
                     visibleNav={visibleNav} hasPerm={hasPerm} isSuperAdmin={isSuperAdmin}
                     brand={brand} logoSrc={logoSrc} />
      </aside>

      {/* Mobile drawer */}
      {drawerOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex" onClick={() => setDrawerOpen(false)}>
          <div className="absolute inset-0 bg-black/50" />
          <div className="relative w-64 bg-card border-r border-border flex flex-col" onClick={(e) => e.stopPropagation()}>
            <SidebarBody collapsed={false} setCollapsed={() => setDrawerOpen(false)}
                         visibleNav={visibleNav} hasPerm={hasPerm} isSuperAdmin={user?.role === "super_admin"}
                         brand={brand} logoSrc={logoSrc} />
          </div>
        </div>
      )}

      {/* Main area */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 md:h-16 topbar-glass sticky top-0 z-30 flex items-center gap-2 md:gap-3 px-3 md:px-6">
          <Button variant="ghost" size="icon" className="md:hidden" onClick={() => setDrawerOpen(true)} data-testid="mobile-menu-btn">
            <Menu className="h-5 w-5" />
          </Button>
          <div className="hidden md:block"><BusinessSwitcher /></div>
          <div className="hidden md:flex flex-1 max-w-md">
            <GlobalSearch />
          </div>
          <div className="flex-1 md:hidden text-center">
            {logoSrc ? (
              <img src={logoSrc} alt={brand?.app_name || "CRM"} className="h-8 mx-auto object-contain" onError={hideOnError} />
            ) : (
              <div className="text-sm font-bold truncate">{brand?.app_name || "CRM Suite"}</div>
            )}
          </div>

          {/* Spacer to push icon group to the far right on desktop */}
          <div className="hidden md:block flex-1" />

          {/* Right-side icon cluster: dark-mode + notifications + profile */}
          <div className="flex items-center gap-1 md:gap-2 ml-auto" data-testid="topbar-actions">
            <Button variant="ghost" size="icon" onClick={toggle} data-testid="theme-toggle-btn" title="Toggle theme"
                    className="rounded-full">
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
            <NavLink to="/notifications" className="relative" data-testid="notifications-btn">
              <Button variant="ghost" size="icon" className="rounded-full">
                <Bell className="h-4 w-4" />
                {unread > 0 && <Badge className="absolute -top-1 -right-1 h-4 min-w-4 px-1 text-[10px] bg-accent text-accent-foreground">{unread}</Badge>}
              </Button>
            </NavLink>
            <DropdownMenu>
              <DropdownMenuTrigger data-testid="user-menu-trigger">
                <Avatar className="h-8 w-8 md:h-9 md:w-9 cursor-pointer ring-2 ring-transparent hover:ring-accent transition">
                  <AvatarImage src={resolveAsset(user?.avatar_url)} onError={hideOnError} />
                  <AvatarFallback className="bg-accent/20 text-accent font-bold">{(user?.name?.[0] || "U").toUpperCase()}</AvatarFallback>
                </Avatar>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="flex flex-col">
                    <span className="text-sm font-semibold">{user?.name}</span>
                    <span className="text-xs text-muted-foreground">@{user?.username || user?.email}</span>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="profile-menu-item"><UserIcon className="h-4 w-4 mr-2" />Profile</DropdownMenuItem>
                <DropdownMenuItem className="text-xs uppercase tracking-wide text-muted-foreground" disabled>Role: {user?.role?.replace("_", " ")}</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} data-testid="logout-btn" className="text-destructive cursor-pointer">
                  <LogOut className="h-4 w-4 mr-2" /> Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <div className="md:hidden px-3 py-2 border-b border-border">
          <BusinessSwitcher />
        </div>

        <main className="flex-1 p-3 md:p-6 lg:p-8 fade-in pb-32 md:pb-8">
          <Outlet />
        </main>

        {/* Mobile bottom nav */}
        <nav className="md:hidden mobile-bottom-nav" data-testid="mobile-bottom-nav">
          {MOBILE_NAV.map((n) => (
            <NavLink key={n.to} to={n.to} data-testid={n.testId}
                     className={({ isActive }) => (isActive ? "active" : "")}>
              <n.icon className="h-5 w-5" />
              <span>{n.label}</span>
              {n.to === "/notifications" && unread > 0 && (
                <span className="absolute top-1 right-4 h-2 w-2 bg-accent rounded-full" />
              )}
            </NavLink>
          ))}
        </nav>
      </div>
    </div>
  );
}

function SidebarBody({ collapsed, setCollapsed, visibleNav, hasPerm, isSuperAdmin, brand, logoSrc }) {
  const appName = brand?.app_name || "CRM Suite";
  return (
    <>
      <div className="h-16 flex items-center px-4 border-b border-border">
        <Link to="/dashboard" className="flex items-center gap-2 min-w-0">
          {logoSrc ? (
            <img src={logoSrc} alt={appName} className={`${collapsed ? "h-9 w-9" : "h-10"} object-contain`} onError={hideOnError} />
          ) : (
            <>
              <div className="h-8 w-8 rounded-lg flex items-center justify-center font-black text-white text-sm shrink-0"
                   style={{ background: `linear-gradient(135deg, ${brand?.primary_color || "#21095b"}, var(--brand-color-light, #FFA35C))` }}>
                {(appName[0] || "C").toUpperCase()}
              </div>
              {!collapsed && <span className="font-bold text-lg tracking-tight truncate">{appName}</span>}
            </>
          )}
        </Link>
      </div>
      <nav className="p-3 space-y-0.5 flex-1 overflow-y-auto">
        {visibleNav.map((n) => (
          n.group === "leads"
            ? <LeadsGroup key="leads-group" collapsed={collapsed} />
            : (
              <NavLink key={n.to} to={n.to} end={n.end === true} data-testid={n.testId}
                       className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}>
                <n.icon className="h-4 w-4 shrink-0" />
                {!collapsed && <span className="truncate">{n.label}</span>}
              </NavLink>
            )
        ))}

        {hasPerm("settings.view") && (
          <>
            <div className={`${collapsed ? "hidden" : "block"} pt-4 pb-1 px-3 text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground`}>Settings</div>
            {SETTINGS_LINKS.map((n) => (
              <NavLink key={n.to} to={n.to} data-testid={n.testId}
                       className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}>
                <n.icon className="h-4 w-4 shrink-0" />
                {!collapsed && <span className="truncate">{n.label}</span>}
              </NavLink>
            ))}
          </>
        )}
        {isSuperAdmin && (
          <>
            <div className={`${collapsed ? "hidden" : "block"} pt-4 pb-1 px-3 text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground`}>Super Admin</div>
            <NavLink to="/settings/branding" data-testid="nav-branding"
                     className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}>
              <Palette className="h-4 w-4 shrink-0" />
              {!collapsed && <span className="truncate">System Branding</span>}
            </NavLink>
            <NavLink to="/activity-log" data-testid="nav-activity-log"
                     className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}>
              <Activity className="h-4 w-4 shrink-0" />
              {!collapsed && <span className="truncate">Activity Log</span>}
            </NavLink>
            <NavLink to="/settings/health" data-testid="nav-health"
                     className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}>
              <Zap className="h-4 w-4 shrink-0" />
              {!collapsed && <span className="truncate">System Health</span>}
            </NavLink>
          </>
        )}
      </nav>
      <div className="p-3 border-t border-border">
        <button onClick={() => setCollapsed((c) => !c)} className="sidebar-link w-full" data-testid="sidebar-collapse-btn">
          <ChevronRight className={`h-4 w-4 shrink-0 transition-transform ${collapsed ? "" : "rotate-180"}`} />
          {!collapsed && <span>Collapse</span>}
        </button>
      </div>
    </>
  );
}

/* ---------------- Leads collapsible dropdown ----------------
   Fetches active lead-statuses for the current business and renders each as a sub-item.
   Excludes any status flagged is_won=true (those show up under the Clients menu).
   Opens automatically when the current URL is under /leads. */
function LeadsGroup({ collapsed }) {
  const { activeId } = useBusiness();
  const location = useLocation();
  const [statuses, setStatuses] = useState([]);
  const [open, setOpen] = useState(true);

  useEffect(() => {
    const params = activeId ? { business_id: activeId } : {};
    api.get("/statuses", { params })
      .then((r) => setStatuses(Array.isArray(r.data) ? r.data : []))
      .catch(() => setStatuses([]));
  }, [activeId]);

  // Auto-expand when a /leads/* route is active
  useEffect(() => {
    if (location.pathname.startsWith("/leads")) setOpen(true);
  }, [location.pathname]);

  // Exclude "won" (converted) statuses — those show under the Clients menu
  const items = statuses.filter((s) => !s.is_won);
  const isActiveGroup = location.pathname.startsWith("/leads");

  if (collapsed) {
    // Collapsed sidebar mode → single icon that links to /leads (no dropdown)
    return (
      <NavLink to="/leads" end data-testid="nav-leads"
               className={({ isActive }) => `sidebar-link ${isActive || isActiveGroup ? "active" : ""}`}>
        <Users className="h-4 w-4 shrink-0" />
      </NavLink>
    );
  }

  return (
    <div className="space-y-0.5">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="nav-leads"
        className={`sidebar-link w-full flex items-center justify-between ${isActiveGroup ? "active" : ""}`}
      >
        <span className="flex items-center gap-3 min-w-0">
          <Users className="h-4 w-4 shrink-0" />
          <span className="truncate">Leads</span>
        </span>
        <ChevronDown className={`h-3.5 w-3.5 shrink-0 transition-transform ${open ? "" : "-rotate-90"}`} />
      </button>
      {open && (
        <div className="ml-3 pl-3 border-l border-border/60 space-y-0.5" data-testid="nav-leads-submenu">
          {items.length === 0 && (
            <div className="px-2 py-1.5 text-[11px] text-muted-foreground italic">
              Add statuses in Statuses &amp; Sources
            </div>
          )}
          {items.map((s) => (
            <NavLink
              key={s.name}
              to={`/leads/status/${encodeURIComponent(s.name)}`}
              data-testid={`nav-leads-status-${s.name}`}
              className={({ isActive }) => `sidebar-link py-1.5 text-[13px] ${isActive ? "active" : ""}`}
              title={s.label}
            >
              <span
                className="h-2 w-2 rounded-full shrink-0"
                style={{ background: s.color || "#94a3b8" }}
              />
              <span className="truncate">{s.label}</span>
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}
